import { useState } from 'react';
import Dialog from './Dialog';
import { Text } from '../general/Text';
import { en } from '../../locales/en';
import { AsyncAction } from '../../types/general/asyncAction';
import { useGcsStore } from '../../store/useGcsStore';
import type { VehicleId } from '../../types/gcs/vehicle';
import type { MissionId } from '../../types/gcs/mission';
import { useLocalizedErrorMessage } from '../../hooks/useLocalizedErrorMessage';
import { getShortMissionId, getShortVehicleId } from '../../utils/formatters';

interface ConfirmCancelDockMissionDialogProps {
  gcsId: string;
  vehicleId: VehicleId;
  missionId: MissionId;
  onClose: () => void;
  requeuePackage?: boolean;
}

function ConfirmCancelDockMissionDialog({
  gcsId,
  vehicleId,
  missionId,
  onClose,
  requeuePackage = true
}: ConfirmCancelDockMissionDialogProps) {
  const getLocalizedErrorMessage = useLocalizedErrorMessage();
  const [cancelAction, setCancelAction] = useState<AsyncAction>(AsyncAction.initial());
  const cancelDockPreflight = useGcsStore((state) => state.cancelDockPreflight);
  const vehicleLabel = getShortVehicleId(vehicleId);
  const missionLabel = getShortMissionId(missionId);

  const onConfirmClicked = async () => {
    try {
      setCancelAction(AsyncAction.loading());
      await cancelDockPreflight(gcsId, missionId, requeuePackage);
      setCancelAction(AsyncAction.success(undefined));
      onClose();
    } catch (error) {
      setCancelAction(AsyncAction.failure(error));
    }
  };

  return (
    <Dialog
      title={en.confirmCancelDockMissionDialog.title}
      onClose={onClose}
      primaryButtonProps={{
        text: en.confirmCancelDockMissionDialog.cancelMissionButton,
        callback: onConfirmClicked,
        isLoading: cancelAction.isLoading,
        backgroundColor: 'var(--color-danger)',
        foregroundColor: '#ffffff'
      }}
      errorText={cancelAction.isFailure ? getLocalizedErrorMessage(cancelAction.error) : undefined}
    >
      <div style={{ width: 'var(--dialog-common-width)' }}>
        <Text variant='body1'>
          {en.confirmCancelDockMissionDialog.body
            .replace('{missionId}', missionLabel)
            .replace('{vehicleId}', vehicleLabel)}
        </Text>
      </div>
    </Dialog>
  );
}

export default ConfirmCancelDockMissionDialog;
